import React, { useState, useEffect } from 'react';
import { Dialog, DialogFooter } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';
import CustomerStatusTracker from './CustomerStatusTracker';
import {
  FileText,
  User,
  Building,
  CreditCard,
  Package,
  File,
  Users,
  Trash2,
  Eye,
  Edit,
  AlertTriangle,
  CheckCircle,
  X,
  Store,
  Info,
  Edit2,
  Link2,
  Smartphone,
  Landmark,
  Wallet,
  DollarSign,
  Check,
  FileX,
  FileImage,
  Image as ImageIcon,
  Loader,
  Edit3,
  ClipboardList,
  Clock,
  Building2,
  Settings,
  Hash,
  ClipboardCheck,
  ShieldCheck,
  FilePlus
} from 'lucide-react';
import { OrderStatus, fetchOrderDocument, deleteOrderDocument, generateTxnLink, OrderDetail, OrderDocument } from '../../services/order';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { showToast } from '../../utils/toast';
import { useAppSelector } from '../../store/hooks/reduxHooks';
import { UserRole } from '../../types/auth';
import PASettlementTab from './PASettlementTab';



interface OrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderDetail | null;
  onUpdateOrder?: (updatedOrder: OrderDetail) => Promise<void>;
  onRefreshOrder?: () => Promise<void>;
  canEdit: boolean;
}

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  isOpen,
  onClose,
  order,
  onUpdateOrder,
  onRefreshOrder,
  canEdit
}) => {
  // State for editing UTR info
  const [isEditingUtr, setIsEditingUtr] = useState(false);
  const [utrNumber, setUtrNumber] = useState('');
  const [utrReason, setUtrReason] = useState('');
  const { user } = useAppSelector(state => state.auth);

  // State for transaction ID
  const [transactionId, setTransactionId] = useState('');
  const [isEditingTransactionId, setIsEditingTransactionId] = useState(false);

  // State for deleting documents
  const [documentToDelete, setDocumentToDelete] = useState<OrderDocument | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // State for changing order status
  const [newOrderStatus, setNewOrderStatus] = useState<OrderStatus | ''>('');
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  // State for active tab
  const [activeTab, setActiveTab] = useState('details');

  // State for document viewing and deleting
  const [selectedDocType, setSelectedDocType] = useState<'docReceipt'
    | 'docProduct'
    | 'docCustomerConsent'
    | 'docVehicleChassis'
    | 'docVehicleMotor'
    | 'docRtoAcknowledgement'
    | 'docInsurance'
    | 'customDocument' | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [isConfirmingDocDelete, setIsConfirmingDocDelete] = useState(false);
  const [isRegeneratingLink, setIsRegeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [linkExpiry, setLinkExpiry] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Reset states when order changes
  useEffect(() => {
    if (order) {
      setUtrNumber(order.utr || '');
      setUtrReason(order.utrReason || '');
      setTransactionId(order.transaction?.transactionId || '');
      setNewOrderStatus('');
      setSelectedDocType(null);
      setDocumentUrl(null);

      // Log keyFactStatement data for debugging
      if (order.keyFactStatement) {
        console.log('Debug - keyFactStatement:', order.keyFactStatement);
      } else {
        console.log('Debug - No keyFactStatement data found in order');
      }
    }
  }, [order]);

  // Fetch document when document type changes
  useEffect(() => {
    if (order && selectedDocType) {
      fetchDocumentUrl(order.orderId, selectedDocType);
    }
  }, [selectedDocType, order]);

  // Clear the generated link only when a different order is opened
  useEffect(() => {
    setGeneratedLink(null);
    setLinkExpiry(null);
    setLinkCopied(false);
  }, [order?.orderId]);

  if (!order) return null;

  const showPASettlement =
    user?.role !== UserRole.CLEVERPE_USER &&
    (order.status === 'Verification Pending' || order.status === 'Completed');

  // Helper functions for payment display
  const getPaymentStatusVariant = (status: string): "default" | "outline" | "secondary" | "destructive" | "success" => {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'completed':
      case 'successful':
        return 'success';
      case 'pending':
      case 'in process':
      case 'processing':
        return 'secondary';
      case 'failed':
      case 'declined':
      case 'expired':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case 'credit card':
        return <CreditCard size={16} className="mr-1" />;
      case 'debit card':
        return <CreditCard size={16} className="mr-1" />;
      case 'upi':
        return <Smartphone size={16} className="mr-1" />;
      case 'netbanking':
        return <Landmark size={16} className="mr-1" />;
      case 'wallet':
        return <Wallet size={16} className="mr-1" />;
      case 'cash':
        return <DollarSign size={16} className="mr-1" />;
      default:
        return <CreditCard size={16} className="mr-1" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string, includeTime: boolean = false) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    };

    if (includeTime) {
      return new Intl.DateTimeFormat('en-IN', {
        ...options,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).format(date);
    }

    return new Intl.DateTimeFormat('en-IN', options).format(date);
  };

  // Helper function to convert timestamps to formatted dates
  const formatTimestamp = (timestamp?: number, includeTime: boolean = false) => {
    if (!timestamp) return 'N/A';

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid Date';

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    };

    if (includeTime) {
      return new Intl.DateTimeFormat('en-IN', {
        ...options,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).format(date);
    }

    return new Intl.DateTimeFormat('en-IN', options).format(date);
  };

  // Handle UTR update
  const handleUtrUpdate = async () => {
    if (!order || !onUpdateOrder) return;

    try {


      // Create updatedOrder with only the fields that have changed
      const updatedOrder = {
        ...order,
        txnId: order.transaction?.transactionId || '',
        utrUpdatedAt: new Date().toISOString(),
        utrBy: "Current User" // In real app, get from auth context
      };

      // Only include values that are being updated
      if (utrNumber !== order.utr) {
        updatedOrder.utr = utrNumber;
      }

      if (utrReason !== order.utrReason) {
        updatedOrder.utrReason = utrReason;
      }

      await onUpdateOrder(updatedOrder);
      setIsEditingUtr(false);
    } catch (error) {
      console.error('Error updating UTR:', error);
      // Handle error (show error message)
    }
  };

  // Handle document delete
  // const handleDocumentDelete = async () => {
  //   if (!order || !documentToDelete || !onUpdateOrder) {
  //     setShowDeleteConfirm(false);
  //     setDocumentToDelete(null);
  //     return;
  //   }

  //   try {
  //     // Filter out the document to delete
  //     const updatedDocuments = (order.documents || []).filter(
  //       doc => doc.id !== documentToDelete.id
  //     );

  //     const updatedOrder = {
  //       ...order,
  //       documents: updatedDocuments
  //     };

  //     await onUpdateOrder(updatedOrder);
  //     setShowDeleteConfirm(false);
  //     setDocumentToDelete(null);
  //   } catch (error) {
  //     console.error('Error deleting document:', error);
  //     // Handle error (show error message)
  //   }
  // };

  // Handle order status change
  const handleStatusChange = async () => {
    if (!order || !newOrderStatus || !onUpdateOrder) return;

    if (
      newOrderStatus === "Verification Pending" &&
      !transactionId &&
      !order.transaction?.transactionId
    ) {
      setIsEditingTransactionId(true);
      return;
    }

    const updatedOrder: OrderDetail = {
      ...order,
      status: newOrderStatus,
      transaction: {
        eligibleTenure: order.transaction?.eligibleTenure ?? [],
        transactionId: transactionId || order.transaction?.transactionId,
        transactionStatus: order.transaction?.transactionStatus,
        transactionTime: order.transaction?.transactionTime,
        leafRefNo: order.transaction?.leafRefNo
      }
    };


    await onUpdateOrder(updatedOrder);

    setIsChangingStatus(false);
    setNewOrderStatus('');
  };



  // Handle transaction ID update
  const handleTransactionIdUpdate = async () => {
    if (!order || !onUpdateOrder) return;

    try {


      // Create a properly typed transaction object
      const updatedOrder = {
        ...order,
        txnId: transactionId,
        // If status is already Verification Pending, keep it, otherwise change to Verification Pending
        status: order.status === "Verification Pending" ? order.status : "Verification Pending" as OrderStatus
      };


      await onUpdateOrder(updatedOrder);
      setIsEditingTransactionId(false);
    } catch (error) {
      console.error('Error updating Transaction ID:', error);
      // Handle error
    }
  };

  // Function to view/download invoice
  const handleViewInvoice = () => {
    // Generate invoice URL from order ID
    try {
      // Direct to invoice page with order ID
      const invoiceUrl = `/invoice/${order.orderId}`;
      window.open(invoiceUrl, '_blank');
    } catch (error) {
      console.error("Error opening invoice:", error);
    }
  };

  const extractLink = (r: any): string | undefined =>
    r?.redirectLink || r?.result?.data?.link || r?.data?.link || r?.link || r?.url;

  const handleRegenerateLink = async () => {
    const customer: any = order.customerId;
    const partner: any = order.partnerId;
    const partnerId =
      partner?._id || partner?.id || (typeof partner === 'string' ? partner : undefined);

    const merchantCode =
      typeof order.merchantId === 'string'
        ? order.merchantId
        : (order as any).keyFactStatement?.scheme?.merchantIds?.[0];

    const dob = customer?.dob ? new Date(customer.dob).toISOString().slice(0, 10) : '';

    const payload = {
      amount: order.amount,
      mobileNumber: Number(customer?.mobileNo),
      name: customer?.name,
      email: customer?.email,
      pan: customer?.pan,
      dob,
      employmentType: String(customer?.employmentType),
      declaredIncome: customer?.monthlyIncome,
      callbackUrl: order?.callbackUrl,
      productDetails: order.productDetails,
      merchantId: merchantCode,
      pincode: Number(customer?.addresses?.[0]?.pincode),
    };

    const missing = [
      ...(!partnerId ? ['partnerId'] : []),
      ...Object.entries(payload)
        .filter(([, v]) => v === undefined || v === null || v === '' || Number.isNaN(v))
        .map(([k]) => k),
    ];

    if (missing.length) {
      showToast.error(`Missing data: ${missing.join(', ')}`);
      return;
    }

    setIsRegeneratingLink(true);
    try {
      const result = await generateTxnLink(payload as any, String(partnerId));
      const link = extractLink(result);

      if (link) {
        setGeneratedLink(link);
        setLinkExpiry(result?.expiresIn ?? null);
        setLinkCopied(false);
        showToast.success('Transaction link generated');
      } else {
        showToast.error('Link generated but not found in the response');
      }

      await onRefreshOrder?.();
    } catch (err) {
      showToast.error((err as Error).message);
    } finally {
      setIsRegeneratingLink(false);
    }
  };

  const handleCopyLink = async () => {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = generatedLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Render status badge
  const renderStatusBadge = (status: OrderStatus) => {
    let bgColor = '';
    let textColor = '';

    switch (status) {
      case 'In Process':
        bgColor = 'bg-cyan-100';
        textColor = 'text-cyan-800';
        break;
      case 'Expired':
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
        break;
      case 'Product Verified':
        bgColor = 'bg-teal-100';
        textColor = 'text-teal-800';
        break;
      case 'QR Activated':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'Verification Pending':
        bgColor = 'bg-indigo-100';
        textColor = 'text-indigo-800';
        break;
      case 'Failed':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      case 'Approved':
        bgColor = 'bg-orange-100';
        textColor = 'text-orange-800';
        break;
      case 'Completed':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'Cancelled':
        bgColor = 'bg-red-100';
        textColor = 'text-red-900';
        break;
      default:
        bgColor = 'bg-transparent';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-2 py-1 rounded-full text-sm font-medium ${bgColor} ${textColor} capitalize`}>
        {status}
      </span>
    );
  };

  const renderLenderBadge = (lender: string) => {
    let bgColor = '';
    let textColor = '';

    switch (lender.toLowerCase()) {
      case 'cashe':
        bgColor = 'bg-yellow-500';
        textColor = 'text-white';
        break;
      case 'caprion':
        bgColor = 'bg-blue-500';
        textColor = 'text-white';
        break;
      case 'zype':
        bgColor = 'bg-black';
        textColor = 'text-white';
        break;
      case 'general':
        bgColor = 'bg-blue-500';
        textColor = 'text-white';
        break;
      default:
        bgColor = 'bg-gray-200';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {lender}
      </span>
    );
  };


  const handleEditOrder = () => {
    // Implementation for editing the order
    onClose();
    // Navigate to edit page or open edit modal
  };

  // Add formatFileSize function before other helper functions
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown size';

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Add currentUserRole variable
  const currentUserRole = 'ADMIN'; // Temporary fix, ideally would be from auth context

  // Updated helper function for rendering customer name
  const getCustomerName = () => {
    if (order.customerId?.name) return order.customerId.name;
    if (order.settlement?.customerName) return order.settlement.customerName;
    return 'Customer';
  };

  // Updated helper function for rendering customer initial
  const getCustomerInitial = () => {
    const name = getCustomerName();
    return name.charAt(0);
  };

  // Update the hasUtr function to check both places where UTR might exist
  const hasUtr = () => {
    return Boolean(
      (order.utr && order.utr.trim().length > 0) ||
      (order.settlement?.utr && order.settlement.utr.trim().length > 0)
    );
  };

  // Apply CSS class conditionally
  const cn = (...classes: (string | boolean | undefined)[]) => {
    return classes.filter(Boolean).join(' ');
  };

  // New function to fetch document URL
  const fetchDocumentUrl = async (orderId: string, docType: 'docProduct' | 'docReceipt' | 'docCustomerConsent' | 'docVehicleChassis' | 'docVehicleMotor' | 'docRtoAcknowledgement' | 'docInsurance' | 'customDocument') => {
    setIsLoadingDocument(true);
    try {
      const token = localStorage.getItem("token");
      const url = await fetchOrderDocument(orderId, docType, token);
      setDocumentUrl(url);
    } catch (error) {
      console.error(`Error fetching ${docType}:`, error);
      setDocumentUrl(null);
    } finally {
      setIsLoadingDocument(false);
    }
  };

  // Fix the delete document function for docProduct and docReceipt
  const handleDeleteDocument = async () => {

    if (!order || !selectedDocType) return;

    setIsLoadingDocument(true);
    try {
      const token = localStorage.getItem("token");
      const success = await deleteOrderDocument(order.orderId, selectedDocType, token);

      if (success) {
        setDocumentUrl(null);
        setSelectedDocType(null);
        setIsConfirmingDocDelete(false);

        // Refresh the order if there's an update function
        if (onUpdateOrder) {
          const updatedOrder = {
            ...order,
            txnId: order.transaction?.transactionId, // ✅ Add this line
            ...(selectedDocType === 'docProduct' ? {
              docProduct: "",
              status: "Verification Pending" as OrderStatus
            } : {}),
            ...(selectedDocType === 'docReceipt' ? { docReceipt: "" } : {}),
            ...(selectedDocType === 'docCustomerConsent' ? { docCustomerConsent: "" } : {}),
            ...(selectedDocType === 'docVehicleChassis' ? { docVehicleChassis: "" } : {}),
            ...(selectedDocType === 'docVehicleMotor' ? { docVehicleMotor: "" } : {}),
            ...(selectedDocType === 'docRtoAcknowledgement' ? { docRtoAcknowledgement: "" } : {}),
            ...(selectedDocType === 'docInsurance' ? { docInsurance: "" } : {}),
            ...(selectedDocType === 'customDocument' ? { customDocument: "" } : {}),
          };


          await onUpdateOrder(updatedOrder);
        }
      }
    } catch (error) {
      console.error(`Error deleting ${selectedDocType}:`, error);
    } finally {
      setIsLoadingDocument(false);
    }
  };


  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Order Details: ${order?.orderId || 'Loading...'}`}
      size="xl"
    >
      <div className="space-y-6 max-h-[80vh] overflow-y-auto p-1 no-scrollbar">
        {/* Enhanced Header with Customer Name, Order Status, and Creation Date */}
        <div className="flex items-center mb-6">
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold mr-3">
            {getCustomerInitial()}
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">{getCustomerName()}</h2>
            <div className="flex items-center mt-1 text-sm">
              <span className="text-gray-500 mr-2">OrderID: {order.orderId}</span>

              <div className="flex items-center space-x-1">
                {renderStatusBadge(order.status)}
                {renderLenderBadge(order.lender)}
              </div>

              <span className="ml-2 text-gray-500">
                Created: {formatDate(order.createdAt)}
              </span>
            </div>

          </div>
          {user?.role !== UserRole.CLEVERPE_USER && (
            <div className="ml-auto flex gap-2">
              <Button variant="outline" onClick={handleViewInvoice} className="flex items-center gap-2">
                <FileText size={16} className="text-blue-600" />
                View Invoice
              </Button>
              {canEdit && order.lender?.toLowerCase() === 'caprion' && (
                <Button
                  variant="outline"
                  onClick={handleRegenerateLink}
                  disabled={isRegeneratingLink}
                  className="flex items-center gap-2"
                >
                  {isRegeneratingLink ? (
                    <Loader size={16} className="animate-spin" />
                  ) : (
                    <Link2 size={16} className="text-indigo-600" />
                  )}
                  Regenerate Txn Link
                </Button>
              )}
            </div>
          )}
        </div>

        {generatedLink && (
          <div className="rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="bg-indigo-100 p-1.5 rounded-full">
                  <Link2 size={14} className="text-indigo-600" />
                </div>
                <h4 className="text-sm font-semibold text-gray-900">Transaction Link</h4>
                {linkExpiry && (
                  <span className="flex items-center gap-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-full px-2 py-0.5">
                    <Clock size={11} />
                    Expires in {linkExpiry}
                  </span>
                )}
              </div>
              <button
                onClick={() => setGeneratedLink(null)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Dismiss link"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <input
                readOnly
                value={generatedLink}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 min-w-0 truncate bg-white border border-indigo-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <Button
                size="sm"
                onClick={handleCopyLink}
                className={`flex items-center gap-1 text-white ${linkCopied ? 'bg-green-600 hover:bg-green-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              >
                {linkCopied ? <Check size={14} /> : <FileText size={14} />}
                {linkCopied ? 'Copied' : 'Copy'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(generatedLink, '_blank')}
                className="flex items-center gap-1"
              >
                <Eye size={14} />
                Open
              </Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="details">Order Details</TabsTrigger>
            <TabsTrigger value="customers">Customer Info</TabsTrigger>
            <TabsTrigger value="merchant">Merchant Info</TabsTrigger>
            {user?.role !== UserRole.CLEVERPE_USER && <TabsTrigger value="products">Products</TabsTrigger>}
            {user?.role !== UserRole.CLEVERPE_USER && <TabsTrigger value="documents">Documents</TabsTrigger>}
            <TabsTrigger value="payment">Payment Details</TabsTrigger>
            {order?.lender === 'CAPRION' && user?.role !== UserRole.CLEVERPE_USER && <TabsTrigger value="status">Application Status</TabsTrigger>}
            {user?.role !== UserRole.CLEVERPE_USER && <TabsTrigger value="logs">Logs</TabsTrigger>}
            {showPASettlement && <TabsTrigger value="pa-settlement">PA Settlement</TabsTrigger>}
          </TabsList>



          {/* Order Details Tab */}
          <TabsContent value="details" className="space-y-6 " >
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center mb-4 pb-3 border-b border-gray-100">
                <div className="bg-blue-100 p-2 rounded-full mr-3">
                  <FileText className="text-blue-600" size={18} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Order Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Order ID</p>
                  <p className="font-medium">{order.orderId}</p>
                </div>

                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <div className="flex justify-between items-center">
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Order Status</p>
                      {canEdit && !isChangingStatus && (
                        <button
                          onClick={() => {
                            setIsChangingStatus(true);
                            // If changing to Verification Pending, clear transactionId input
                            setNewOrderStatus('');
                            setTransactionId('');
                          }}
                          className="text-blue-600 hover:text-blue-800 bg-blue-50 p-1 rounded"
                        >
                          <Edit size={14} />
                        </button>
                      )}
                    </div>
                    {isChangingStatus ? (
                      <div className="mt-1 space-y-2">
                        <select
                          value={newOrderStatus}
                          onChange={(e) => setNewOrderStatus(e.target.value as OrderStatus)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Status</option>
                          <option value="In Process">In Process</option>
                          <option value="Expired">Expired</option>
                          <option value="Product Verified">Product Verified</option>
                          <option value="QR Activated">QR Activated</option>
                          <option value="Verification Pending">Verification Pending</option>
                          <option value="Failed">Failed</option>
                          <option value="Approved">Approved</option>
                          <option value="Completed">Completed</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>

                        {/* Show transaction ID field when status is Verification Pending and transactionId is empty */}
                        {newOrderStatus === "Verification Pending" && (
                          // !order.transaction?.transactionId && (
                          <div className="mt-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Transaction ID (required for Verification Pending status)
                            </label>
                            <Input
                              value={transactionId}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTransactionId(e.target.value)}
                              className="h-8 text-sm"
                              placeholder="Enter Transaction ID"
                            />
                          </div>
                        )}

                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            onClick={handleStatusChange}
                            disabled={
                              !newOrderStatus ||
                              (newOrderStatus === "Verification Pending" && (!transactionId || transactionId.trim() === ""))
                            }
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Update
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setIsChangingStatus(false);
                              setNewOrderStatus('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="font-medium">{renderStatusBadge(order.status)}</p>
                    )}
                  </div>
                )}
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Transaction Status</p>
                    <p className="font-medium">{hasUtr() ? 'True' : 'False'}</p>
                  </div>
                )}
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Link Activated At</p>
                    <p className="font-medium">{formatTimestamp(order.linkActivatedAt, true)}</p>
                  </div>
                )}
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Transaction Time</p>
                    <p className="font-medium">{order.transaction?.transactionTime ? formatTimestamp(order.transaction.transactionTime, true) : 'N/A'}</p>
                  </div>
                )}
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <div className="flex justify-between items-center">
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Transaction ID</p>
                      {/* {canEdit && !isEditingTransactionId && (
                        <button
                          onClick={() => setIsEditingTransactionId(true)}
                          className="text-blue-600 hover:text-blue-800 bg-blue-50 p-1 rounded"
                        >
                          <Edit size={14} />
                        </button>
                      )} */}
                    </div>
                    {isEditingTransactionId ? (
                      <div className="mt-1 space-y-2">
                        <Input
                          value={transactionId}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTransactionId(e.target.value)}
                          className="h-8 text-sm"
                          placeholder="Enter Transaction ID"
                        />
                        <div className="flex space-x-2">
                          <Button size="sm" onClick={handleTransactionIdUpdate} disabled={!transactionId} className="bg-blue-600 hover:bg-blue-700">
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setIsEditingTransactionId(false);
                              setTransactionId(order.transaction?.transactionId || '');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="font-medium">{order.transaction?.transactionId || 'Not Available'}</p>
                    )}
                  </div>
                )}
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <div className="flex justify-between items-center">
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">UTR Number</p>
                      {canEdit && !isEditingUtr && (
                        <button
                          onClick={() => setIsEditingUtr(true)}
                          className="text-blue-600 hover:text-blue-800 bg-blue-50 p-1 rounded"
                        >
                          <Edit size={14} />
                        </button>
                      )}
                    </div>
                    {isEditingUtr ? (
                      <div className="mt-1 space-y-2">
                        <Input
                          value={utrNumber}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUtrNumber(e.target.value)}
                          className="h-8 text-sm"
                          placeholder="Enter UTR Number"
                        />
                        <input
                          type="text"
                          value={utrReason}
                          onChange={(e) => setUtrReason(e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter UTR Reason"
                        />
                        <div className="flex space-x-2">
                          <Button size="sm" onClick={handleUtrUpdate} disabled={!utrNumber && !utrReason} className="bg-blue-600 hover:bg-blue-700">
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setIsEditingUtr(false);
                              setUtrNumber(order.utr || '');
                              setUtrReason(order.utrReason || '');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="font-medium">{order.utr || 'Not Available'}</p>
                    )}
                  </div>
                )}
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">UTR Updated At</p>
                    <p className="font-medium">
                      {typeof order.utrUpdatedAt === 'number'
                        ? formatTimestamp(order.utrUpdatedAt, true)
                        : order.utrUpdatedAt
                          ? formatDate(order.utrUpdatedAt, true)
                          : (order.settlement?.utrAt ? formatDate(order.settlement.utrAt, true) : 'Not Updated')}
                    </p>
                  </div>
                )}
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">UTR By</p>
                    <p className="font-medium">{order.utrBy || 'Not Available'}</p>
                  </div>
                )}
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">UTR Reason</p>
                    <p className="font-medium">{order.utrReason || 'Not Available'}</p>
                  </div>
                )}
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Lender</p>
                    <p className="font-medium">{order.lender}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center mb-4 pb-3 border-b border-gray-100">
                <div className="bg-purple-100 p-2 rounded-full mr-3">
                  <Info className="text-purple-600" size={18} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Created At</p>
                  <p className="font-medium">{formatDate(order.createdAt, true)}</p>
                </div>
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Updated At</p>
                    <p className="font-medium">{formatDate(order.updatedAt ?? '', true)}</p>
                  </div>
                )}
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Serial Number</p>
                    <p className="font-medium">{order.serialNo || 'N/A'}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Amount</p>
                  <p className="font-medium">{formatCurrency(order.amount || order.settlement?.totalAmount || 0)}</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Customer Information Tab */}
          <TabsContent value="customers" className="space-y-6">
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center mb-4 pb-3 border-b border-gray-100">
                <div className="bg-green-100 p-2 rounded-full mr-3">
                  <User className="text-green-600" size={18} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Customer Name</p>
                  <p className="font-medium">{order.customerId?.name || order.settlement?.customerName}</p>
                </div>

                <div>
                  <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Mobile Number</p>
                  <p className="font-medium">{order.customerId?.mobileNo || order.settlement?.customerMobileNo}</p>
                </div>

                {user?.role !== UserRole.CLEVERPE_USER && (
                  <>
                    {order.customerId?.monthlyIncome && (
                      <div>
                        <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Income</p>
                        <p className="font-medium">
                          {formatCurrency(order.customerId.monthlyIncome)}
                        </p>
                      </div>
                    )}

                    {order.customerId?.employmentType !== undefined && (
                      <div>
                        <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Employment Type</p>
                        <p className="font-medium">
                          {order.customerId.employmentType === 1
                            ? 'Salaried'
                            : order.customerId.employmentType === 2
                              ? 'Self-Employed'
                              : order.customerId.employmentType === 3
                                ? 'Business Owner'
                                : String(order.customerId.employmentType)}
                        </p>
                      </div>
                    )}
                  </>
                )}

              </div>
            </div>
          </TabsContent>

          {/* Merchant Information Tab */}
          <TabsContent value="merchant" className="space-y-6">
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center mb-4 pb-3 border-b border-gray-100">
                <div className="bg-purple-100 p-2 rounded-full mr-3">
                  <Store className="text-purple-600" size={18} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Merchant Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Merchant Name</p>
                  <p className="font-medium">{order.merchantId?.name || order.settlement?.merchantName}</p>
                </div>

                <div>
                  <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Mobile Number</p>
                  <p className="font-medium">{order.merchantId?.mobileNo || order.settlement?.merchantMobileNo}</p>
                </div>
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Email</p>
                    <p className="font-medium">{order.merchantId?.email || order.settlement?.merchantEmail || 'Not Available'}</p>
                  </div>
                )}


                {user?.role !== UserRole.CLEVERPE_USER && order.merchantId?.kycBank && (
                  <>
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Bank Account</p>
                      <p className="font-medium">{order.merchantId.kycBank.accountNo || 'Not Available'}</p>
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">IFSC Code</p>
                      <p className="font-medium">{order.merchantId.kycBank.ifsc || 'Not Available'}</p>
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Account Holder</p>
                      <p className="font-medium">{order.merchantId.kycBank.holderName || 'Not Available'}</p>
                    </div>
                  </>
                )}

              </div>
            </div>

            {/* Partner Information (if available) */}
            {order.partnerId && (
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center mb-4 pb-3 border-b border-gray-100">
                  <div className="bg-yellow-100 p-2 rounded-full mr-3">
                    <Users className="text-yellow-600" size={18} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Partner Information</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Partner Name</p>
                    <p className="font-medium">{order.partnerId.name || order.settlement?.partnerName}</p>
                  </div>
                  {user?.role !== UserRole.CLEVERPE_USER && (
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Mobile Number</p>
                      <p className="font-medium">{order.partnerId.mobileNo}</p>
                    </div>
                  )}
                  {user?.role !== UserRole.CLEVERPE_USER && (
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">Email</p>
                      <p className="font-medium">{order.partnerId.email || 'Not Available'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Products Tab */}
          {user?.role !== UserRole.CLEVERPE_USER && (
            <TabsContent value="products" className="space-y-6">
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center mb-4 pb-3 border-b border-gray-100">
                  <div className="bg-red-100 p-2 rounded-full mr-3">
                    <Package className="text-red-600" size={18} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Product Details</h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Variation
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="bg-white">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {order.product?.name || order.settlement?.productName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.variationString || order.settlement?.productVariation || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {formatCurrency(order.amount || order.settlement?.totalAmount || 0)}
                        </td>
                      </tr>
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={2} className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                          Order Total:
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                          {formatCurrency(order.amount || order.settlement?.totalAmount || 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </TabsContent>
          )}

          {/* Documents Tab */}
          {user?.role !== UserRole.CLEVERPE_USER && (
            <TabsContent value="documents" className="space-y-6">
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center mb-4 pb-3 border-b border-gray-100">
                  <div className="bg-indigo-100 p-2 rounded-full mr-3">
                    <FileText className="text-indigo-600" size={18} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Order Documents</h3>
                </div>

                <div className="flex flex-col md:flex-row gap-6">
                  {/* Document Type Selection */}
                  <div className="w-full md:w-1/3 space-y-4">
                    <h4 className="font-medium text-gray-700">Document Type</h4>

                    <div className="space-y-3">
                      {/* Product Image */}
                      <button
                        className={`w-full text-left p-3 rounded-lg border flex items-center space-x-3 transition-colors ${selectedDocType === 'docProduct'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        onClick={() => setSelectedDocType('docProduct')}
                      >
                        <div className="bg-blue-100 p-2 rounded-full">
                          <FileImage size={18} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">Product Image</p>
                          <p className="text-xs text-gray-500">View the product image</p>
                        </div>
                      </button>

                      {/* Receipt */}
                      <button
                        className={`w-full text-left p-3 rounded-lg border flex items-center space-x-3 transition-colors ${selectedDocType === 'docReceipt'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        onClick={() => setSelectedDocType('docReceipt')}
                      >
                        <div className="bg-green-100 p-2 rounded-full">
                          <File size={18} className="text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">Receipt</p>
                          <p className="text-xs text-gray-500">View payment receipt</p>
                        </div>
                      </button>

                      {/* ✅ Customer Consent (conditionally rendered) */}
                      {order?.partnerId?.isEv && (
                        <>
                          <button
                            className={`w-full text-left p-3 rounded-lg border flex items-center space-x-3 transition-colors ${selectedDocType === 'docCustomerConsent'
                              ? 'border-purple-500 bg-purple-50 text-purple-700'
                              : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            onClick={() => setSelectedDocType('docCustomerConsent')}
                          >
                            <div className="bg-purple-100 p-2 rounded-full">
                              <FileText size={18} className="text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium">Customer Consent</p>
                              <p className="text-xs text-gray-500">View customer consent document</p>
                            </div>
                          </button>

                          <button
                            className={`w-full text-left p-3 rounded-lg border flex items-center space-x-3 transition-colors ${selectedDocType === 'docVehicleChassis'
                              ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                              : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            onClick={() => setSelectedDocType('docVehicleChassis')}
                          >
                            <div className="bg-yellow-100 p-2 rounded-full">
                              <Hash size={18} className="text-yellow-600" />
                            </div>
                            <div>
                              <p className="font-medium">Vehicle Chassis</p>
                              <p className="text-xs text-gray-500">
                                View vehicle chassis document
                              </p>
                            </div>
                          </button>
                          <button
                            className={`w-full text-left p-3 rounded-lg border flex items-center space-x-3 transition-colors ${selectedDocType === 'docVehicleMotor'
                              ? 'border-pink-500 bg-pink-50 text-pink-700'
                              : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            onClick={() => setSelectedDocType('docVehicleMotor')}
                          >
                            <div className="bg-pink-100 p-2 rounded-full">
                              <Settings size={18} className="text-pink-600" />
                            </div>
                            <div>
                              <p className="font-medium">Vehicle Motor</p>
                              <p className="text-xs text-gray-500">
                                View vehicle motor document
                              </p>
                            </div>
                          </button>


                        </>
                      )}
                      {(order?.subCategory === 'HIGH-SPEED') && (
                        <>
                          <button
                            className={`w-full text-left p-3 rounded-lg border flex items-center space-x-3 transition-colors ${selectedDocType === 'docRtoAcknowledgement'
                              ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                              : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            onClick={() => setSelectedDocType('docRtoAcknowledgement')}
                          >
                            <div className="bg-blue-100 p-2 rounded-full">
                              <ClipboardCheck size={18} className="text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium">RTO Acknowledgement</p>
                              <p className="text-xs text-gray-500">
                                View RTO acknowledgment document
                              </p>
                            </div>
                          </button>


                          <button
                            className={`w-full text-left p-3 rounded-lg border flex items-center space-x-3 transition-colors ${selectedDocType === 'docInsurance'
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            onClick={() => setSelectedDocType('docInsurance')}
                          >
                            <div className="bg-green-100 p-2 rounded-full">
                              <ShieldCheck size={18} className="text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium">Insurance</p>
                              <p className="text-xs text-gray-500">
                                View insurance document
                              </p>
                            </div>
                          </button>

                        </>
                      )}
                    </div>
                    {typeof order?.customDocument === 'string' &&
                      order.customDocument.trim().length > 0 && (
                        <button
                          className={`w-full text-left p-3 rounded-lg border flex items-center space-x-3 transition-colors ${selectedDocType === 'customDocument'
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          onClick={() => setSelectedDocType('customDocument')}
                        >
                          <div className="bg-green-100 p-2 rounded-full">
                            <FilePlus size={18} className="text-green-600" />
                          </div>

                          <div>
                            <p className="font-medium">
                              Custom Doc:{order?.customDocumentName || 'Custom Document'}
                            </p>
                            <p className="text-xs text-gray-500">
                              View {order?.customDocumentName || 'document'}
                            </p>
                          </div>
                        </button>

                      )}

                  </div>

                  {/* Document Display Area */}
                  <div className="w-full md:w-2/3 border rounded-lg p-4 min-h-[400px] flex items-center justify-center bg-gray-50">
                    {isLoadingDocument ? (
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <Loader className="animate-spin mb-3" size={32} />
                        <p>Loading document...</p>
                      </div>
                    ) : selectedDocType ? (
                      documentUrl ? (
                        <div className="w-full h-full flex flex-col">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="font-medium">
                              {selectedDocType === 'docProduct'
                                ? 'Product Image'
                                : selectedDocType === 'docReceipt'
                                  ? 'Receipt'
                                  : 'Customer Consent'}

                              {/* Show upload date */}
                              <span className="text-sm text-gray-500 font-normal">
                                {selectedDocType === 'docProduct' && order?.docProductAt
                                  ? ` • Uploaded on ${new Date(order.docProductAt).toLocaleDateString('en-GB')}`
                                  : selectedDocType === 'docReceipt' && order?.docReceiptAt
                                    ? ` • Uploaded on ${new Date(order.docReceiptAt).toLocaleDateString('en-GB')}`
                                    : selectedDocType === 'docCustomerConsent' && order?.docCustomerConsentAt
                                      ? ` • Uploaded on ${new Date(order.docCustomerConsentAt).toLocaleDateString('en-GB')}`
                                      : ''}
                              </span>

                            </h4>
                            <div className="flex space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(documentUrl, '_blank')}
                                className="flex items-center gap-1"
                              >
                                <Eye size={16} />
                                Open
                              </Button>

                              {canEdit && (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => setIsConfirmingDocDelete(true)}
                                  className="flex items-center gap-1"
                                >
                                  <Trash2 size={16} />
                                  Delete
                                </Button>
                              )}
                            </div>
                          </div>

                          <div className="flex-grow rounded-lg overflow-hidden bg-white border border-gray-200 flex items-center justify-center">
                            <img
                              src={documentUrl}
                              alt={
                                selectedDocType === 'docProduct'
                                  ? 'Product Image'
                                  : selectedDocType === 'docReceipt'
                                    ? 'Receipt'
                                    : 'Customer Consent'
                              }
                              className="max-w-full max-h-[500px] object-contain"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center text-gray-500">
                          <FileX size={48} className="mb-3" />
                          <p className="font-medium">Document not available</p>
                          <p className="text-sm">The requested document could not be found</p>
                        </div>
                      )
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <ImageIcon size={48} className="mb-3" />
                        <p className="font-medium">Select a document to view</p>
                        <p className="text-sm">Choose a document type from the left panel</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
          )}

          {/* Payment Tab */}
          <TabsContent value="payment" className="space-y-6">
            {user?.role !== UserRole.CLEVERPE_USER && (
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="flex items-center mb-4 pb-3 border-b border-gray-100">
                  <div className="bg-green-100 p-2 rounded-full mr-3">
                    <CreditCard className="text-green-600" size={18} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Loan Details</h3>
                </div>

                {/* Loan Details Content */}
                {order.keyFactStatement ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

                    {/* Scheme Name */}
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Scheme Name
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {order.keyFactStatement?.scheme?.name || "N/A"}
                        </p>
                        {order.keyFactStatement?.scheme && (
                          <span
                            className={`px-2 py-0.5 text-[10px] font-medium rounded-md ${order.keyFactStatement.scheme.interestBased
                              ? "bg-orange-200 text-orange-700" // Low cost
                              : "bg-green-200 text-green-700"   // No cost
                              }`}
                          >
                            {order.keyFactStatement.scheme.interestBased ? "Low cost" : "No cost"}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Gross Loan Amount */}
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Order Amount
                      </p>
                      <p className="font-medium">
                        {formatCurrency(order.keyFactStatement?.amount || order.amount || 0)}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Net Loan Amount
                      </p>
                      <p className="font-medium">
                        {formatCurrency(order.keyFactStatement?.calculations?.netLoanAmount || order.amount || 0)}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Down Payment
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.totalDownPayment
                          ? formatCurrency(order.keyFactStatement.calculations.totalDownPayment)
                          : "N/A"}
                      </p>
                    </div>

                    {/* Scheme Tenure */}
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Loan Tenure
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.NoOfNetEmis
                          ? `${order.keyFactStatement.calculations.NoOfNetEmis} months`
                          : "N/A"}
                      </p>
                    </div>

                    {/* EMI Amount */}
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        EMI Amount (Monthly)
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.monthlyEMI
                          ? formatCurrency(order.keyFactStatement.calculations.monthlyEMI)
                          : "N/A"}
                      </p>
                    </div>

                    {/* Advanced EMI */}
                    {/* <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Advanced EMI
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.scheme?.advancedEMI !== undefined
                          ? order.keyFactStatement.scheme.advancedEMI
                          : "N/A"}
                      </p>
                    </div> */}

                    {/* Advanced EMI Payment */}
                    {/* <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Advanced EMI Payment
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.advancedEmiPayment !== undefined
                          ? formatCurrency(order.keyFactStatement.calculations.advancedEmiPayment)
                          : "N/A"}
                      </p>
                    </div> */}

                    {/* Net Disbursement */}
                    {/* <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Net Disbursement
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.netDisbursement !== undefined
                          ? formatCurrency(order.keyFactStatement.calculations.netDisbursement)
                          : "N/A"}
                      </p>
                    </div> */}

                    {/* Loan Tenure */}
                    {/* <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Loan Tenure
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.grossTenure
                          ? `${order.keyFactStatement.calculations.grossTenure} months`
                          : "N/A"}
                      </p>
                    </div> */}

                    {/* Annual Interest */}
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Annual Interest
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.scheme?.annualInterest !== undefined
                          ? `${order.keyFactStatement.scheme.annualInterest}%`
                          : "N/A"}
                      </p>
                    </div>

                    {/* Interest On Loan */}
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Interest On Loan
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.totalInterest !== undefined
                          ? formatCurrency(order.keyFactStatement.calculations.totalInterest)
                          : "N/A"}
                      </p>
                    </div>

                    {/* Processing Fee */}
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Processing Fee (incl. GST)
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.totalProcessingFee !== undefined
                          ? formatCurrency(order.keyFactStatement.calculations.totalProcessingFee)
                          : "N/A"}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Net Settlement by Lender to PA
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.netSettlementByLenderToPA !== undefined
                          ? formatCurrency(order.keyFactStatement.calculations.netSettlementByLenderToPA)
                          : "N/A"}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Net Settlement to Merchant from PA
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.netSettlementToMerchantFromPA !== undefined
                          ? formatCurrency(order.keyFactStatement.calculations.netSettlementToMerchantFromPA)
                          : "N/A"}
                      </p>
                    </div>

                    {/* GST */}
                    {/* <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        GST
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.scheme?.GST !== undefined
                          ? formatCurrency(order.keyFactStatement.scheme.GST)
                          : "N/A"}
                      </p>
                    </div> */}

                    {/* Total Amount */}
                    {/* <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Total Amount
                      </p>
                      <p className="font-medium text-green-600">
                        {formatCurrency(order.amount || 0)}
                      </p>
                    </div> */}

                    {/* Number of EMIs */}
                    {/* <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Number of EMIs
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.NoOfNetEmis || "N/A"}
                      </p>
                    </div> */}

                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Merchant Subvention Cost
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.merchantSubCost !== undefined
                          ? formatCurrency(order.keyFactStatement.calculations.merchantSubCost)
                          : "N/A"}
                      </p>
                    </div>

                    {/* DBD To Dealer */}
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Merchant Gets
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.merchantGets !== undefined
                          ? formatCurrency(order.keyFactStatement.calculations.merchantGets)
                          : "N/A"}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Merchant Total Deduction
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.calculations?.merchantTotalDeduction !== undefined
                          ? formatCurrency(order.keyFactStatement.calculations.merchantTotalDeduction)
                          : "N/A"}
                      </p>
                    </div>

                    {/* Subvention Cost */}
                    {/* <div className="col-span-3">
                      <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
                        Subvention Cost
                      </p>
                      <p className="font-medium">
                        {order.keyFactStatement?.scheme?.subventionCost !== undefined
                          ? `${order.keyFactStatement.scheme.subventionCost}%`
                          : "N/A"}
                      </p>
                    </div> */}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <AlertTriangle size={24} className="text-amber-500" />
                    </div>
                    <p className="text-gray-800 font-medium mb-1">No loan details available</p>
                    <p className="text-gray-500 text-sm text-center max-w-md">
                      The Key Fact Statement for this order is missing or not available. This might happen for orders created before the feature was implemented.
                    </p>
                  </div>
                )}
              </div>
            )}


            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <h4 className="text-sm font-medium text-gray-800 mb-4">UTR Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">UTR Number</p>
                  {isEditingUtr && user?.role !== UserRole.CLEVERPE_USER ? (
                    <div className="flex items-center space-x-2 mt-1">
                      <Input
                        value={utrNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUtrNumber(e.target.value)}
                        className="h-8 text-sm"
                        placeholder="Enter UTR Number"
                      />
                      <Button size="sm" variant="outline" onClick={handleUtrUpdate}>
                        <Check size={14} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setIsEditingUtr(false)}>
                        <X size={14} />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <p className="font-medium mr-2">{order.utr || order.settlement?.utr || 'N/A'}</p>
                      {canEdit && user?.role !== UserRole.CLEVERPE_USER && (
                        <Button size="sm" variant="ghost" className="px-1.5 h-6" onClick={() => setIsEditingUtr(true)}>
                          <Edit size={14} />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">UTR Updated At</p>
                  <p className="font-medium">
                    {typeof order.utrUpdatedAt === 'number'
                      ? formatTimestamp(order.utrUpdatedAt, true)
                      : order.utrUpdatedAt
                        ? formatDate(order.utrUpdatedAt, true)
                        : (order.settlement?.utrAt ? formatDate(order.settlement.utrAt, true) : 'Not Updated')}
                  </p>
                </div>
                {user?.role !== UserRole.CLEVERPE_USER && (
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">UTR Reason</p>
                    <p className="font-medium">{order.utrReason || order.settlement?.utrReason || 'Not Available'}</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {user?.role !== UserRole.CLEVERPE_USER && (
            <TabsContent value="status" className="space-y-6">
              <CustomerStatusTracker order={order} activeTab={activeTab} />
            </TabsContent>
          )}


          {/* Logs Tab */}
          {user?.role !== UserRole.CLEVERPE_USER && (
            <TabsContent value="logs" className="space-y-6">
              <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700">Logs</h3>
                </div>

                {order.logs && order.logs.length > 0 ? (
                  <div className="divide-y divide-gray-200 max-h-[400px] overflow-y-auto">
                    {order.logs.slice().reverse().map((log, index) => (
                      <div key={index} className="p-4 hover:bg-gray-50">
                        <div className="flex items-start">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-3 flex-shrink-0">
                            <ClipboardList size={16} />
                          </div>
                          <div>
                            <p className="text-sm text-gray-700">{log}</p>
                            <div className="mt-1 flex items-center text-xs text-gray-500">
                              <Clock size={12} className="mr-1" />
                              <span>
                                {log.includes(" at ") ? log.split(" at ")[1] : ""}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-4">
                      <ClipboardList size={24} />
                    </div>
                    <p className="text-gray-500 text-sm">No logs are available for this order</p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {showPASettlement && (
            <TabsContent value="pa-settlement" className="space-y-6">
              <PASettlementTab
                order={order}
                canEdit={canEdit}
                onRefresh={onRefreshOrder}
              />
            </TabsContent>
          )}
        </Tabs>


      </div>

      {/* Delete Document Confirmation Dialog */}
      {isConfirmingDocDelete && selectedDocType && (
        <>
          {console.log("Rendering confirmation dialog for document deletion")}

          <Dialog
            isOpen={isConfirmingDocDelete}
            onClose={() => setIsConfirmingDocDelete(false)}
            title="Confirm Document Deletion"
            size="sm"
          >
            <div className="p-4 space-y-4">
              <div className="flex items-start">
                <AlertTriangle size={24} className="text-red-500 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium">Are you sure you want to delete this document?</h4>
                  <p className="text-gray-500 mt-1">
                    You are about to delete the {selectedDocType === 'docProduct' ? 'product image' : 'receipt'}.
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsConfirmingDocDelete(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    console.log("Delete Document button clicked");
                    handleDeleteDocument();
                  }}
                  disabled={isLoadingDocument}
                >
                  {isLoadingDocument ? (
                    <>
                      <Loader size={16} className="animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Document'
                  )}
                </Button>
              </div>
            </div>
          </Dialog>
        </>
      )}

      {/* Original Delete Document Confirmation Dialog */}
      {/* {showDeleteConfirm && documentToDelete && (
        <Dialog
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setDocumentToDelete(null);
          }}
          title="Confirm Document Deletion"
          size="sm"
        >
          <div className="p-4 space-y-4">
            <div className="flex items-start">
              <AlertTriangle size={24} className="text-red-500 mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-medium">Are you sure you want to delete this document?</h4>
                <p className="text-gray-500 mt-1">
                  You are about to delete the document "{documentToDelete.label}". This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDocumentToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDocumentDelete}
              >
                Delete Document
              </Button>
            </div>
          </div>
        </Dialog>
      )} */}
    </Dialog>
  );
}; 